import { Image, IImageProps } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Skeleton } from './skeleton/skeleton';

interface Props extends IImageProps {
    wrapperClassname?: string;
    showEmptyImageThumbnail?: boolean;
    enableImageSwitchingEffect?: boolean;
    onClick?: () => void;
    location: string;
    telemetry: ITelemetry;
    variantConcatenatedId?: string;
}

export const ImageWithLoading = ({
    wrapperClassname,
    showEmptyImageThumbnail = false,
    enableImageSwitchingEffect = false,
    onClick,
    location,
    telemetry,
    variantConcatenatedId,
    ...imageProps
}: Props) => {
    const slotRefs = useRef<(HTMLDivElement | null)[]>([]);
    const loadTokenRef = useRef(0);

    const [isLoaded, setIsLoaded] = useState(false);
    const [activeSlot, setActiveSlot] = useState<0 | 1>(0);
    const [slotSrcs, setSlotSrcs] = useState([imageProps.src, undefined]);
    const [slotToWatch, setSlotToWatch] = useState<0 | 1 | null>(imageProps.src ? 0 : null);

    useEffect(() => {
        if (!imageProps.src) {
            setIsLoaded(true);
            setSlotToWatch(null);
            return;
        }

        const currentVisibleSrc = slotSrcs[activeSlot];

        if (currentVisibleSrc === imageProps.src && isLoaded) {
            setSlotToWatch(null);
            return;
        }

        if (!isLoaded) {
            setSlotSrcs(prev => {
                if (prev[activeSlot] === imageProps.src) {
                    return prev;
                }

                const next = [...prev];
                next[activeSlot] = imageProps.src;
                return next;
            });

            setSlotToWatch(activeSlot);
            return;
        }

        const nextSlot = activeSlot === 0 ? 1 : 0;

        if (slotSrcs[nextSlot] === imageProps.src) {
            setActiveSlot(nextSlot);
            setSlotToWatch(null);
            return;
        }

        setSlotSrcs(prev => {
            if (prev[nextSlot] === imageProps.src) {
                return prev;
            }

            const next = [...prev];
            next[nextSlot] = imageProps.src;
            return next;
        });

        setSlotToWatch(nextSlot);
    }, [imageProps.src]);

    useEffect(() => {
        if (slotToWatch === null || !imageProps.src) {
            return;
        }

        const wrapper = slotRefs.current[slotToWatch];
        if (!wrapper) {
            return;
        }

        const currentToken = ++loadTokenRef.current;
        let resolved = false;
        let img: HTMLImageElement | null = null;

        const markLoaded = () => {
            if (resolved || loadTokenRef.current !== currentToken) {
                return;
            }

            resolved = true;
            cleanupImgListener();

            setIsLoaded(true);

            if (slotToWatch !== activeSlot) {
                setActiveSlot(slotToWatch);
            }

            setSlotToWatch(null);
            observer.disconnect();
        };

        const markFailed = () => {
            if (resolved || loadTokenRef.current !== currentToken) {
                return;
            }

            const message = `[image-with-loading][${location}] Image failed to load (variantId: ${variantConcatenatedId ?? 'unknown'}) (src: ${imageProps.src})`;
            telemetry.error(message);

            markLoaded();
        };

        const cleanupImgListener = () => {
            if (img) {
                img.removeEventListener('load', check);
                img.removeEventListener('error', markFailed);
            }
        };

        const check = () => {
            if (resolved) {
                return;
            }

            const found = wrapper.querySelector('img');

            if (!(found instanceof HTMLImageElement)) {
                return;
            }

            if (img !== found) {
                cleanupImgListener();

                img = found;
                img.addEventListener('load', check);
                img.addEventListener('error', markFailed);
            }

            const rawSrc = imageProps.src ?? '';
            const matchesRequestedSrc = (found.currentSrc || found.src).includes(rawSrc);

            if (matchesRequestedSrc && found.complete) {
                // The image may have finished (or failed) before our listeners were attached,
                // e.g. preloaded images that error before hydration. A failed image is still
                // 'complete', but has no natural dimensions.
                if (found.naturalWidth === 0) {
                    markFailed();
                } else {
                    markLoaded();
                }
            }
        };

        const observer = new MutationObserver(check);

        observer.observe(wrapper, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['src', 'srcset']
        });

        check();

        return () => {
            resolved = true;
            observer.disconnect();
            cleanupImgListener();
        };
    }, [slotToWatch, imageProps.src, telemetry, location, variantConcatenatedId]);

    const isFirstLoad = !isLoaded && activeSlot === 0 && !slotSrcs[1];
    const isUsingMultipleSlots = !!slotSrcs[0] && !!slotSrcs[1];
    const isSwitchingImage = slotToWatch !== null && slotToWatch !== activeSlot;

    const renderSlot = (slotIndex: 0 | 1) => (
        <div
            ref={el => {
                slotRefs.current[slotIndex] = el;
            }}
            onClick={onClick}
            className={classnames('image-with-loading__slot', {
                'image-with-loading__slot--active': activeSlot === slotIndex,
                'image-with-loading__slot--inactive': activeSlot !== slotIndex,
                'image-with-loading__slot--switching-image': enableImageSwitchingEffect && activeSlot === slotIndex && isSwitchingImage
            })}
        >
            <Image {...imageProps} src={slotSrcs[slotIndex]} className={classnames(imageProps.className, 'image-with-loading__image')} />
        </div>
    );

    return (
        <div
            className={classnames('image-with-loading', wrapperClassname, {
                'image-with-loading--show-empty-thumbnail': showEmptyImageThumbnail
            })}
        >
            {isFirstLoad && <Skeleton />}
            {renderSlot(0)}
            {isUsingMultipleSlots && renderSlot(1)}
        </div>
    );
};
